import React, {useEffect, useState} from 'react';
import {StyleSheet} from 'react-native';
import {HStack, Text, VStack} from 'native-base';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {
  FieldErrors,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import {useSalesmanContext} from '../../../../../../context/salesman';
import useToast from '../../../../../../hooks';
import {IProductUnit} from '../../../../../../context/user/Types';
import {
  formatCurrencyValue,
  formatNumber,
} from '../../../../../../utils/format';
import {message as t} from '../../../../../../utils/messages';
import {TextInput} from '../../../../../../components/inputs/text';
import InternalTemplate from '../../../../../../components/templates/internal';
import {StackParamList} from '../../../../../../navigators';
import {SpinnerModal} from '../../../../../../components/modals';
import {PrimaryButton} from '../../../../../../components/buttons';
import {
  DropDownInput,
  ReadonlyInput,
} from '../../../../../../components/inputs';
import IDropDownOption from '../../../../../../interfaces/ui/dropdown-option.interface';
import {SearchInputLauncher} from '../../../../../../components/inputs/search/launcher/SearchInputLauncher';
import {CurrencyOptionEnum} from '../../../../../../constants/enumerations';

interface IOrderItemToAdd {
  unityId: string | null;
  count: number | null;
}

type ScreenProps = NativeStackScreenProps<StackParamList, 'SalesmanOrderAdd'>;

const SalesmanOrderAddScreen = ({navigation, route}: ScreenProps) => {
  const {salesmanProductToAdd, updateCandidateToShoppingCart, customerOrder} =
    useSalesmanContext();
  const [isLoading, setIsLoading] = useState(false);
  const [unitList, setUnitList] = useState([] as IDropDownOption[]);
  const [price, setPrice] = useState('');
  const [total, setTotal] = useState('');
  const [subUnitCount, setSubUnitCount] = useState('');
  const [subUnitLabel, setSubUnitLabel] = useState('');
  const [iva, setIva] = useState('');
  const {showInputsError} = useToast();

  const {
    setValue,
    handleSubmit,
    getValues,
    control,
    watch,
    reset,
    formState: {errors},
  } = useForm({
    defaultValues: {
      unityId: '',
      count: null,
    } as IOrderItemToAdd,
  });

  const unitSelectId = watch('unityId');
  const count = watch('count');

  useEffect(() => {
    fillPriceValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  useEffect(() => {
    fillPriceValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitSelectId]);

  useEffect(() => {
    setIsLoading(false);
  }, [route]);

  useEffect(() => {
    if (salesmanProductToAdd == null) {
      navigation.navigate('SalesmanProductSearch', route.params);
    } else {
      setUnitList(getUnitList());
      setValue('unityId', '');
      setValue('count', '' as any);
      setDefaultValues();
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesmanProductToAdd]);

  const handleGoBack = () => {
    if (customerOrder!.items!.length > 0) {
      navigation.pop(1);
      return;
    }
    navigation.pop(2);
  };

  const getProductTitle = () => {
    if (salesmanProductToAdd) {
      return salesmanProductToAdd.title;
    }
    return '';
  };

  const getUnitList = () => {
    if (salesmanProductToAdd) {
      const list = salesmanProductToAdd.units.map(unit => {
        return {
          label: unit.title,
          value: unit.id,
        } as IDropDownOption;
      });
      return [
        {
          label: t('salesman-order-default-unit-label'),
          value: '',
        } as IDropDownOption,
        ...list,
      ];
    }
    return [];
  };

  const findPriceByUnit = (unitCount: number, unit: IProductUnit) => {
    return unit.price.find(unitPrice => {
      return (
        unitPrice.units.min <= unitCount && unitCount <= unitPrice.units.max
      );
    });
  };

  const setDefaultValues = () => {
    setPrice('');
    setSubUnitCount('');
    setSubUnitLabel('');
    setIva('');
  };

  const fillPriceValue = () => {
    if (salesmanProductToAdd) {
      const unitId: string = getValues('unityId') ?? '';
      const unit = salesmanProductToAdd.units.find(ele => ele.id === unitId);
      if (unit) {
        if (count) {
          const unitPrice = findPriceByUnit(count, unit);
          if (unitPrice) {
            setPrice(formatNumber(unitPrice?.value));
            setSubUnitLabel(`Total ${unit.subUnit.title}`);
            setSubUnitCount(formatNumber(count * unit.subUnit.size).toString());
            setIva(
              formatCurrency(
                (count * unitPrice.value * salesmanProductToAdd.ivaPercent) /
                  100,
              ),
            );
            const subTotal = count * unitPrice.value;
            setTotal(
              formatCurrency(
                subTotal + subTotal * (salesmanProductToAdd.ivaPercent / 100),
              ),
            );
          }
        } else {
          setSubUnitCount('');
        }
      }
      return;
    }
    setPrice('');
  };

  const formatCurrency = (value: number | string, isNumber = true) => {
    return formatCurrencyValue(value, getCurrency(), isNumber);
  };

  const handleOnContinueSubmit: SubmitHandler<IOrderItemToAdd> = async () => {
    if (salesmanProductToAdd) {
      const unitId: string = getValues('unityId') ?? '';
      const unit = salesmanProductToAdd.units.find(ele => ele.id === unitId);
      try {
        if (count) {
          if (unit) {
            const unitPrice = findPriceByUnit(count, unit);
            if (unitPrice) {
              updateCandidateToShoppingCart({
                product: salesmanProductToAdd,
                unit,
                count: Number(count),
                price: unitPrice?.value,
              });
              navigation.pop(1);
              return;
            }
          }
        }
      } catch (e) {}
    }
    showInputsError();
  };

  const onInvalid: SubmitErrorHandler<IOrderItemToAdd> = async (
    fieldErrors: FieldErrors<IOrderItemToAdd>,
  ) => {
    if (fieldErrors) {
      showInputsError();
    }
  };

  const getPercent = () => {
    return salesmanProductToAdd
      ? formatNumber(salesmanProductToAdd.ivaPercent)
      : '';
  };

  const getCurrency = () => {
    return customerOrder?.foreign
      ? CurrencyOptionEnum.foreign
      : CurrencyOptionEnum.local;
  };

  return (
    <InternalTemplate
      title={'salesman-order-add-title'}
      contentPaddingX="0"
      backButton={{
        onPress: () => handleGoBack(),
        label: 'go-back',
        usage: 'external',
      }}>
      <SpinnerModal show={isLoading} />

      <VStack marginX={5}>
        <SearchInputLauncher
          label="salesman-order-add-search-product-input"
          defaultValue={getProductTitle()}
          navigateTo={'SalesmanProductSearch'}
        />
        <DropDownInput
          control={control}
          name="unityId"
          label="Unidades "
          options={unitList}
          error={errors.unityId}
          rules={{
            required: 'add-products-products-required',
          }}
        />
        <HStack justifyContent={'space-between'}>
          <TextInput
            type="number"
            label={'Cantidad'}
            error={errors.count}
            name={'count'}
            control={control}
            width={'45%'}
            rules={{
              required: 'add-products-products-required',
              pattern: {
                value: new RegExp('^[0-9]+$'),
                message: 'add-products-count-products-only-numbers',
              },
            }}
          />
          {subUnitCount !== '' && (
            <ReadonlyInput
              label={subUnitLabel}
              value={subUnitCount}
              icon={undefined}
              width={'45%'}
              backgroundColor="#EFEFEF"
            />
          )}
        </HStack>
        {subUnitCount !== '' && (
          <HStack justifyContent={'space-between'}>
            <ReadonlyInput
              label={`Precio ${
                customerOrder && customerOrder.foreign ? '$' : 'Bs.'
              }`}
              value={price}
              icon={undefined}
              width={'45%'}
              backgroundColor="#EFEFEF"
            />

            <ReadonlyInput
              label={'IVA %'}
              value={getPercent()}
              icon={undefined}
              width={'45%'}
              backgroundColor="#EFEFEF"
            />
          </HStack>
        )}

        {count && unitSelectId && (
          <VStack style={styles.summaryBox}>
            <HStack justifyContent={'space-between'} style={styles.summaryItem}>
              <Text style={styles.summaryItemLabel}>
                {t('salesman-order-add-summary-price')}
              </Text>
              <Text style={styles.summaryItemValue}>
                {formatCurrency(price)}
              </Text>
            </HStack>
            <HStack justifyContent={'space-between'} style={styles.summaryItem}>
              <Text style={styles.summaryItemLabel}>
                {t('salesman-order-add-summary-iva')} - {getPercent()}
              </Text>
              <Text style={styles.summaryItemValue}>{iva}</Text>
            </HStack>
            <HStack justifyContent={'space-between'} style={styles.summaryItem}>
              <Text style={styles.summaryItemLabel}>
                {t('salesman-order-add-summary-total')}
              </Text>
              <Text style={styles.summaryItemValue}>{total}</Text>
            </HStack>

            <VStack paddingX={5} marginY={5}>
              <PrimaryButton
                label="salesman-order-add-button"
                onPress={handleSubmit(handleOnContinueSubmit, onInvalid)}
              />
            </VStack>
          </VStack>
        )}
      </VStack>
    </InternalTemplate>
  );
};
export default SalesmanOrderAddScreen;
const styles = StyleSheet.create({
  summaryItem: {
    borderBottomWidth: 1,
    borderColor: '#0000001F',
    marginBottom: 7,
    marginTop: 5,
  },
  summaryItemLabel: {
    color: '#00000099',
  },
  summaryItemLabelTotal: {
    color: '#000000DE',
  },
  summaryItemValue: {
    color: '#000000DE',
  },
  summaryBox: {
    backgroundColor: '#F1F3F4',
    padding: 22,
    borderRadius: 10,
    marginTop: 30,
    marginBottom: 150,
  },
  companyIcon: {
    marginLeft: 10,
  },
});
